import React from "react";
import { PaymentProofBadge } from "@components/PaymentProof/PaymentProofBadge";
import { useTranslation } from "@/hooks/useTranslation";

const formatDateTime = (date) => (date ? new Date(date).toLocaleString("fr-FR") : "");

const fullName = (user) => [user?.firstname, user?.lastname].filter(Boolean).join(" ") || user?.email;

/**
 * The proofs sent for one invoice, newest first. `renderActions(proof)` lets the admin page add
 * its accept / reject buttons under each proof.
 */
export const PaymentProofHistory = ({ proofs, renderActions }) => {
    const { t } = useTranslation();

    if (!proofs?.length) {
        return <p className="text-sm text-gray-500">{t("invoices.proof.noProofs")}</p>;
    }

    return (
        <ul className="space-y-3">
            {proofs.map((proof) => {
                const uploader = fullName(proof.uploadedBy);
                return (
                    <li key={proof.id} className="border border-gray-200 rounded-lg p-4">
                        <div className="flex flex-wrap items-center justify-between gap-2">
                            <div className="flex items-center gap-3 min-w-0">
                                <i className={`fas ${proof.contentType === "application/pdf" ? "fa-file-pdf text-red-500" : "fa-file-image text-blue-500"} text-xl`}></i>
                                <div className="min-w-0">
                                    <a
                                        href={proof.fileUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="font-medium text-blue-600 hover:text-blue-800 truncate block"
                                    >
                                        {proof.fileName || t("invoices.proof.view")}
                                    </a>
                                    <div className="text-xs text-gray-500">
                                        {t("invoices.proof.sentOn", { date: formatDateTime(proof.uploadedAt) })}
                                        {uploader && <> {t("invoices.proof.sentBy", { name: uploader })}</>}
                                    </div>
                                </div>
                            </div>
                            <PaymentProofBadge proof={proof} />
                        </div>

                        {proof.status === "REJECTED" && proof.rejectionReason && (
                            <p className="mt-3 text-sm text-red-700 bg-red-50 rounded p-2">
                                <strong>{t("invoices.proof.reasonLabel")}</strong> {proof.rejectionReason}
                            </p>
                        )}
                        {proof.reviewedAt && (
                            <div className="mt-2 text-xs text-gray-500">
                                {t("invoices.proof.reviewedOn", { date: formatDateTime(proof.reviewedAt) })}
                            </div>
                        )}

                        {renderActions && renderActions(proof)}
                    </li>
                );
            })}
        </ul>
    );
};
